import { MonitorFPS } from './monitors/MonitorFPS';
import { MonitorAudio } from './monitors/MonitorAudio';
import { MonitorDevice } from './monitors/MonitorDevice';
import { MonitorTextures } from './monitors/MonitorTextures';
import { MonitorBase } from './monitors/_MonitorBase';
import { MonitorVersions } from './monitors/MonitorVersions';

const DEFAULT_MONITORS = [
  { key: 'versions', component: MonitorVersions },
  { key: 'fps', component: MonitorFPS },
  { key: 'audio', component: MonitorAudio },
  { key: 'device', component: MonitorDevice },
  { key: 'textures', component: MonitorTextures },
];

/**
 *
 * @alias components.core.MonitorPlugin
 * @classdesc A Monitor component for monitoring different aspects of teh game, such as fps
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.monitor
 * </pre>
 *
 * **Log Monitors**
 *
 * Some monitors may provide more datail when they are logged into the console - for example, textures will log all the textures in this.game.textures
 *
 * To log a specific monitor you can call
 *
 * <pre>
 * this.game.monitor.logMonitor(id);
 * </pre>
 *
 *
 * **Default Monitors**
 *
 * The default plugin has four monitors built in
 *
 * - FPS
 * - Device
 * - Textures
 * - Audio
 *
 * **Override Default Monitors**
 *
 * You can override any of the default models by extending their base class, or by extending the MonitorBase class itself
 * This is done via the component options when calling [getConfig()]{@link getConfigBase} in your base game
 *
 * <pre>
 * components : {
 *  monitor : {
 *    data : {
 *      monitors : [
 *        { key: 'fps', component : NEW_FPS_MONITOR_CLASS }
 *      ]
 *    }
 *  }
 * }
 * </pre>
 *
 * **Custom Monitors**
 *
 * You can also add your own custom monitors via the same way as overrriding default ones
 * This is done via the component options when calling [getConfig()]{@link getConfigBase} in your base game
 *
 * <pre>
 * components : {
 *  monitor : {
 *    data : {
 *      monitors : [
 *        { key: 'new_monitor', component : NEW_MONITOR_CLASS }
 *      ]
 *    }
 *  }
 * }
 * </pre>
 *
 * **Position Monitors**
 *
 * By default, the monitor will position itself in the bottom left of the game window
 * You can override this either via the component options when calling [getConfig()]{@link getConfigBase} in your base game
 *
 * <pre>
 * components : {
 *  monitor : {
 *   position : value
 *  }
 * }
 * </pre>
 *
 * or you can control the position via code
 *
 * <pre>
 * this.game.monitor.positionMonitors(value);
 * </pre>
 *
 * The plugin supports the following positions
 *
 *  - left
 *  - right
 *  - top
 *  - topleft
 *  - topright
 *  - bottom
 *  - bottomLeft
 *  - bottomRight
 *
 */
class MonitorPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
    this._monitors = [];
    this._position = 'bottomleft';
    this._enabled = false;
  }

  init(config) {
    this._position = config?.position || this._position;
  }

  addDefaultMonitors() {
    for (let i = 0; i < DEFAULT_MONITORS.length; i++) {
      this.addMonitor(DEFAULT_MONITORS[i].key, DEFAULT_MONITORS[i].component);
    }
  }

  enable() {
    this._enabled = true;
    if (this._monitors.length > 0) {
      this._domContainer.style.display = 'flex';
    }
  }

  disable() {
    this._enabled = false;
    this._domContainer.style.display = 'none';
  }

  isEnabled() {
    return this._enabled;
  }

  /**
   * add a monitor to the system
   * @param {Object} config monitor configuration
   * @param {string} [config.key]  key for referencing monitor, will replace existing monitors if duplicate key used
   * @param {MonitorBase} [config.component]  monitor class file to initialize when adding, should extend monitor base
   * @param {bool} [config.enabled=false]  if the monitor should automatically be enabled when added
   * @param {Object} [config.config=false]  optional configuration object that can be passed through to the monitor in initialization
   *
   * @see{@link MonitorBase}
   *
   */
  addMonitor(key, component, config = {}) {
    if (!this._domContainer) this._injectDomContainer();
    if (this._enabled) {
      this._domContainer.style.display = 'flex';
    }
    this.removeMonitor(key);
    this._monitors.push(new Monitor(this.game, this._domContainer, key, component, config));
  }

  removeMonitor(key) {
    const existingIndex = this._monitors.findIndex((m) => m.id === key);
    if (existingIndex >= 0) {
      this._monitors[existingIndex].shutdown();
      this._monitors.splice(existingIndex, 1);
    }
    if (this._monitors.length == 0 && this._domContainer) {
      this._domContainer.style.display = 'none';
    }
  }

  // =============================================================================================
  // MONITOR FINDERS
  // =============================================================================================

  /**
   * @description get a monitor
   * @param {string} id the id of the monitor to get
   */
  getMonitor(id) {
    return this._monitors.find((m) => m.id === id);
  }

  /**
   * @description get an array of all monitor keys
   */
  getMonitorKeys() {
    return Object.keys(this._monitorConfig);
  }

  /**
   * @description trigger a monitor log - logs results on console
   * @param {string} id the id of the monitor to log
   */
  logMonitor(id) {
    const monitor = this.getMonitor(id);
    if (monitor) monitor.logMonitor();
    else console.log(`[MONITOR LOG] : ${id} not found`);
  }

  // =============================================================================================
  // DOM ELEMENTS
  // =============================================================================================

  _injectDomContainer() {
    if (this._domContainer) return;
    this._domContainer = document.createElement('div');
    this._domContainer.style.background = 'rgba(0,0,0,0.75)';
    this._domContainer.style.color = '#fff';
    this._domContainer.style.position = 'absolute';
    this._domContainer.style.boxSizing = 'border-box';
    this._domContainer.style.fontSize = '100%';
    this._domContainer.style.fontFamily = 'sans-serif';
    this._domContainer.style.display = this._enabled ? 'flex' : 'none';
    this._domContainer.style.flexWrap = 'wrap';
    this._domContainer.style.pointerEvents = 'none';

    this.game.canvas.parentElement.appendChild(this._domContainer);

    this.positionMonitors(this._position);
  }

  /**
   * @description position the monitor container
   * @param {string} value the desired position (top, topleft, topright, left, right, bottom, bottomleft, bottomright )
   *
   */
  positionMonitors(value) {
    this._position = value;
    if (!this._domContainer) return;

    const position = {};

    switch (value) {
      case 'topleft':
        position.top = '0';
        position.bottom = 'auto';
        position.left = '0';
        position.right = 'auto';
        position.transform = '';
        position.flexDirection = 'row';
        position.justifyContent = 'center';
        break;
      case 'top':
        position.top = '0';
        position.bottom = 'auto';
        position.left = '50%';
        position.right = 'auto';
        position.transform = 'translateX(-50%)';
        position.flexDirection = 'row';
        position.justifyContent = 'center';
        break;
      case 'topright':
        position.top = '0';
        position.bottom = 'auto';
        position.left = 'auto';
        position.right = '0';
        position.transform = '';
        position.flexDirection = 'row';
        position.justifyContent = 'center';
        break;
      case 'bottomright':
        position.top = 'auto';
        position.bottom = '0';
        position.left = 'auto';
        position.right = '0';
        position.transform = '';
        position.flexDirection = 'row';
        position.justifyContent = 'center';
        break;
      case 'bottom':
        position.top = 'auto';
        position.bottom = '0';
        position.left = '50%';
        position.right = 'auto';
        position.transform = 'translateX(-50%)';
        position.flexDirection = 'row';
        position.justifyContent = 'center';
        break;
      case 'left':
        position.top = '50%';
        position.bottom = 'auto';
        position.left = '0';
        position.right = 'auto';
        position.transform = 'translateY(-50%)';
        position.flexDirection = 'column';
        position.justifyContent = 'left';
        break;
      case 'right':
        position.top = '50%';
        position.bottom = 'auto';
        position.left = 'auto';
        position.right = '0';
        position.transform = 'translateY(-50%)';
        position.flexDirection = 'column';
        position.justifyContent = 'right';
        break;
      default:
        position.top = 'auto';
        position.bottom = '0';
        position.left = '0';
        position.right = 'auto';
        position.transform = '';
        position.flexDirection = 'row';
        position.justifyContent = 'center';
        break;
    }

    Object.keys(position).forEach((key) => (this._domContainer.style[key] = position[key]));
  }
}

// =============================================================================================
// HELPER CLASS
// =============================================================================================

class Monitor {
  constructor(game, container, id, component, config) {
    this._id = id;
    this._component = new component(game, container, config);
  }

  get id() {
    return this._id;
  }

  get component() {
    return this._component;
  }

  logMonitor() {
    console.log(`[MONITOR LOG] : ${this._id}\n\n${this._component.getLog()}`);
  }
}

export { MonitorPlugin };
